import { usePage } from "@inertiajs/react"
import { usePostHog } from "posthog-js/react"
import type { ReactNode } from "react"
import { useLayoutEffect, useRef } from "react"

interface PostHogIdentityProviderProps {
  children: ReactNode
}

/**
 * Ties the PostHog browser session to the signed-in user.
 *
 * Reads `auth.user` from Inertia's shared props on every page visit, so a
 * client-side sign-in is picked up without a reload. Signed-in users are
 * identified and recorded; anonymous visitors are never identified and never
 * recorded (inertia.tsx inits with `disable_session_recording: true`). When the
 * user goes from signed in to not — signing out, or an Inertia `<Link>` from
 * the dashboard onto a public check-in form — recording stops and the identity
 * resets. The PostHog client itself comes from `PostHogProvider` in
 * inertia.tsx; with analytics off (`analytics` prop null) this does nothing.
 *
 * Browsing as someone else (ADR 0008) is a switch from one signed-in person
 * to another, twice: developer to target, target to developer. posthog-js's
 * `identify` under an already-identified different id sends no `$identify`
 * and keeps the same replay session, so the two people would share one
 * recording. Recording is stopped and the client reset before the new
 * identify, which closes the first replay and opens a new session. The
 * distinct id stays the account that acted (ADR 0003: the server names the
 * target too); `impersonated_by` is a super property on every browser event
 * and the replay, registered after the reset because reset clears them.
 *
 * @example
 * ```tsx
 * // In persistent-layout.tsx
 * <PostHogIdentityProvider>
 *   {children}
 * </PostHogIdentityProvider>
 * ```
 */
export function PostHogIdentityProvider({
  children,
}: PostHogIdentityProviderProps) {
  const posthog = usePostHog()
  const { auth, analytics, impersonation } = usePage().props
  const userId = auth?.user?.id
  const userName = auth?.user?.name
  const impersonatorId = impersonation?.impersonator_id ?? null
  const previousUserId = useRef(userId)

  // A layout effect, not a passive one: React runs a child's passive effects
  // before its parent's, so the page's own useEffect (the check-in form's
  // checkin_form_viewed) would otherwise capture under the old identity.
  // Every layout effect runs before any passive effect, so the reset lands
  // first.
  useLayoutEffect(() => {
    if (!analytics) return
    const previous = previousUserId.current

    if (userId) {
      if (previous && previous !== userId) {
        // One signed-in person to another: close their replay, forget them.
        posthog.stopSessionRecording()
        posthog.reset()
      }
      // MUST be the server's user id; the name is what the person page shows.
      posthog.identify(String(userId), { name: userName })
      if (impersonatorId) {
        posthog.register({ impersonated_by: String(impersonatorId) })
      } else {
        posthog.unregister("impersonated_by")
      }
      if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording()
    } else if (previous) {
      posthog.stopSessionRecording()
      posthog.reset() // forget the previous user's identity
    }
    previousUserId.current = userId
  }, [posthog, analytics, userId, userName, impersonatorId])

  return children
}
